import { relativeLuminance } from "@/lib/brandContrast";

/**
 * Logo visibility check (org-wide-logo, D16/D17): the employee app's home header
 * shows the organisation's logo directly on the main colour with no box, so a
 * transparent logo whose mark is close to the main colour disappears. This
 * module measures that; it NEVER blocks saving.
 *
 * Kept apart from `brandContrast.ts` on purpose: those colour warnings share
 * test vectors with the employee app; this check is CMS-only.
 */

/** A pixel counts as part of the logo when its alpha is at least this (0–255). */
export const LOGO_OPAQUE_ALPHA = 128;
/** At or above this share of opaque pixels the logo carries its own background. */
export const LOGO_OWN_BACKGROUND_RATIO = 0.9;
/** Warn when the median contrast of the logo's opaque pixels vs the main colour is below this. */
export const LOGO_MIN_CONTRAST = 3.0;
/** Images are downscaled to at most this width before their pixels are read. */
export const LOGO_MAX_SAMPLE_WIDTH = 256;

export type LogoAnalysis = "ok" | "low-contrast";

export type LogoPixels =
  | { status: "ok"; data: Uint8ClampedArray; width: number; height: number }
  | { status: "unavailable" };

/** sRGB channel (0–255) → linear light, precomputed (WCAG 2.x formula, as in brandContrast). */
const LINEAR = Array.from({ length: 256 }, (_, c) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
});

/**
 * Pure analysis of an RGBA buffer against the main colour (`#RRGGBB`).
 * - Opaque pixels (alpha ≥ 128) / ALL pixels ≥ 0.9 ⇒ the logo has its own background ⇒ "ok".
 * - Otherwise "low-contrast" when the median contrast ratio of the opaque pixels vs `main` is < 3.0.
 * An empty buffer is "ok" (nothing to judge); a fully transparent image is "low-contrast" (nothing visible).
 */
export function analyseLogoPixels(rgba: Uint8ClampedArray, main: string): LogoAnalysis {
  const total = Math.floor(rgba.length / 4);
  if (total === 0) return "ok";

  const mainLum = relativeLuminance(main);
  const ratios: number[] = [];
  for (let i = 0; i < total; i++) {
    const o = i * 4;
    if (rgba[o + 3] < LOGO_OPAQUE_ALPHA) continue;
    const lum = 0.2126 * LINEAR[rgba[o]] + 0.7152 * LINEAR[rgba[o + 1]] + 0.0722 * LINEAR[rgba[o + 2]];
    const [hi, lo] = lum >= mainLum ? [lum, mainLum] : [mainLum, lum];
    ratios.push((hi + 0.05) / (lo + 0.05));
  }

  if (ratios.length / total >= LOGO_OWN_BACKGROUND_RATIO) return "ok";
  if (ratios.length === 0) return "low-contrast";

  ratios.sort((a, b) => a - b);
  const mid = Math.floor(ratios.length / 2);
  const median = ratios.length % 2 === 1 ? ratios[mid] : (ratios[mid - 1] + ratios[mid]) / 2;
  return median < LOGO_MIN_CONTRAST ? "low-contrast" : "ok";
}

/**
 * Adds the `lc=1` cache-buster so the browser re-requests a saved logo in CORS
 * mode instead of reusing a cached non-CORS response (which would taint the
 * canvas). Placed before any fragment; data:/blob: URLs are left alone.
 */
export function withLogoCacheBuster(url: string): string {
  if (/^(data|blob):/i.test(url)) return url;
  const hashAt = url.indexOf("#");
  const base = hashAt === -1 ? url : url.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : url.slice(hashAt);
  return `${base}${base.includes("?") ? "&" : "?"}lc=1${hash}`;
}

function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = withLogoCacheBuster(url);
  });
}

function readPixels(source: CanvasImageSource, naturalWidth: number, naturalHeight: number): LogoPixels {
  if (!naturalWidth || !naturalHeight) return { status: "unavailable" };
  const width = Math.min(LOGO_MAX_SAMPLE_WIDTH, naturalWidth);
  const height = Math.max(1, Math.round((naturalHeight * width) / naturalWidth));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return { status: "unavailable" };
  try {
    ctx.drawImage(source, 0, 0, width, height);
    // Throws a SecurityError when the image is cross-origin without CORS (tainted canvas).
    const { data } = ctx.getImageData(0, 0, width, height);
    return { status: "ok", data, width, height };
  } catch {
    return { status: "unavailable" };
  }
}

/**
 * Reads a logo's pixels (downscaled to ≤ 256 px wide).
 * - Pending `File`: decoded with `createImageBitmap`.
 * - Saved URL: an `Image` with `crossOrigin="anonymous"` + cache-buster.
 * Any failure (decode/load error, no 2D context, SecurityError) ⇒ `{ status: "unavailable" }`.
 */
export async function loadLogoPixels(source: File | string): Promise<LogoPixels> {
  if (typeof source === "string") {
    const img = await loadImage(source);
    if (!img) return { status: "unavailable" };
    return readPixels(img, img.naturalWidth, img.naturalHeight);
  }

  if (typeof createImageBitmap !== "function") return { status: "unavailable" };
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(source);
  } catch {
    return { status: "unavailable" };
  }
  try {
    return readPixels(bitmap, bitmap.width, bitmap.height);
  } finally {
    bitmap.close?.();
  }
}
